///
/// EarthBrowser
///
/// Created by Matt Giger on 4/21/2024
/// Copyright (c) 2024 Geodata Labs
///

import { cross, dot, normal, vec3 } from "./vector"

/**
 * Represents a quaternion in 3D space.
 */
export class quaternion {
	/**
	 * Creates a quaternion from an angle and an axis.
	 * @param angle - The angle in radians.
	 * @param axis - The axis to rotate around.
	 * @returns A new quaternion.
	 */
	public static fromAngleAxis(angle: number, axis: vec3): quaternion {
		const ang = angle * 0.5
		const s = Math.sin(ang)
		return new quaternion(s * axis.x, s * axis.y, s * axis.z, Math.cos(ang))
	}

	/**
	 * Creates a quaternion from one vec3 to another.
	 * @param from - The starting normalized vector.
	 * @param to - The ending normalized vector.
	 * @returns A quaternion representing the rotation from the starting vector to the ending vector.
	 */
	public static fromVec3ToVec3(from: vec3, to: vec3): quaternion {
		const dotProduct = dot(from, to)

		// Handle the case where vectors are parallel
		if (Math.abs(dotProduct) > 0.9999) {
			if (dotProduct > 0) {
				// Vectors are the same direction - return identity quaternion
				return new quaternion(0, 0, 0, 1)
			} else {
				// Vectors are opposite - need to find perpendicular axis
				// This is a degenerate case that needs special handling
				const temp = Math.abs(from.x) < 0.9 ? vec3.unitX : vec3.unitY
				const axis = normal(cross(from, temp))
				return new quaternion(axis.x, axis.y, axis.z, 0) // 180 degree rotation
			}
		} else {

			const axis = normal(cross(from, to))
			const angle = Math.acos(dotProduct)
			const halfAngle = angle * 0.5
			const s = Math.sin(halfAngle)
			const c = Math.cos(halfAngle)

			return new quaternion(s * axis.x, s * axis.y, s * axis.z, c)
		}
	}

	public readonly x: number
	public readonly y: number
	public readonly z: number
	public readonly w: number

	/**
	 * Creates a new quaternion.
	 * @param x - The x component of the quaternion's vector part.
	 * @param y - The y component of the quaternion's vector part.
	 * @param z - The z component of the quaternion's vector part.
	 * @param w - The scalar part of the quaternion.
	 */
	constructor(x: number, y: number, z: number, w: number) {
		this.x = x
		this.y = y
		this.z = z
		this.w = w
	}

	/**
	 * Multiplies two quaternions.
	 * @param q - The quaternion to multiply.
	 * @returns A new quaternion resulting from the multiplication.
	 */
	public multiply(q: quaternion): quaternion {
		return new quaternion(
			this.w * q.x + this.x * q.w + this.y * q.z - this.z * q.y,
			this.w * q.y + this.y * q.w + this.z * q.x - this.x * q.z,
			this.w * q.z + this.z * q.w + this.x * q.y - this.y * q.x,
			this.w * q.w - this.x * q.x - this.y * q.y - this.z * q.z
		)
	}

	/**
	 * Normalizes the quaternion to unit length.
	 * @returns A new normalized quaternion.
	 */
	public normalize(): quaternion {
		const length = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w)
		if (length === 0) {
			return new quaternion(0, 0, 0, 1)
		}
		return new quaternion(this.x / length, this.y / length, this.z / length, this.w / length)
	}

	/**
	 * Returns the inverse of the quaternion.
	 * @returns A new quaternion representing the inverse.
	 */
	public inverse(): quaternion {
		return new quaternion(-this.x, -this.y, -this.z, this.w)
	}

	/**
	 * Returns the dot product of the quaternion.
	 * @param q - The quaternion to dot product with.
	 * @returns The dot product.
	 */
	public dot(q: quaternion): number {
		return this.x * q.x + this.y * q.y + this.z * q.z + this.w * q.w
	}

	/**
	 * Raises the quaternion to a power, effectively scaling the rotation angle.
	 * @param power - The power to raise the quaternion to (e.g., 0.5 for half rotation).
	 * @returns A new quaternion representing the scaled rotation.
	 */
	public pow(power: number): quaternion {
		if (Math.abs(this.w) > 0.9999) {
			// Quaternion is close to identity
			return new quaternion(0, 0, 0, 1)
		}

		const angle = 2 * Math.acos(Math.abs(this.w))
		const newAngle = angle * power
		const s = Math.sin(newAngle / 2)
		const c = Math.cos(newAngle / 2)

		// Preserve the sign of w
		const sign = this.w >= 0 ? 1 : -1
		const axisLength = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z)

		if (axisLength === 0) {
			return new quaternion(0, 0, 0, 1)
		}

		return new quaternion(
			s * this.x / axisLength,
			s * this.y / axisLength,
			s * this.z / axisLength,
			sign * c
		)
	}

	/**
	 * Rotates a vector by this quaternion.
	 * @param v - The vector to rotate.
	 * @returns The rotated vector.
	 */
	public rotateVector(v: vec3): vec3 {
		// Convert vector to quaternion (w=0)
		const qv = new quaternion(v.x, v.y, v.z, 0)

		// q * v * q^-1
		const result = this.multiply(qv).multiply(this.inverse())

		return new vec3(result.x, result.y, result.z)
	}

	/**
	 * Returns a string representation of the quaternion.
	 * @returns A string in the format "quat(w, x, y, z)".
	 */
	public toString(): string {
		return `quat(${this.w}, ${this.x}, ${this.y}, ${this.z})`
	}
}
